/**
 * Reader enhancements. Pages work fully without JavaScript; features that need it
 * (likes, contact, report) are hidden in the HTML and revealed here.
 */

const $$ = <T extends Element>(selector: string, root: ParentNode = document) => [...root.querySelectorAll<T>(selector)];

/* --------------------------- Menus (details) --------------------------- */

// Only one dropdown open at a time; close on outside click and on Escape.
const dropdowns = $$<HTMLDetailsElement>('.dropdown, .share');
dropdowns.forEach((d) =>
  d.addEventListener('toggle', () => {
    if (d.open) dropdowns.forEach((other) => other !== d && (other.open = false));
  }),
);
document.addEventListener('click', (event) => {
  const target = event.target as Node;
  dropdowns.forEach((d) => d.open && !d.contains(target) && (d.open = false));
});
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  for (const d of [...dropdowns, ...$$<HTMLDetailsElement>('.nav-mobile')]) {
    if (d.open) {
      d.open = false;
      d.querySelector('summary')?.focus();
    }
  }
});

// Mobile menu: lock background scrolling while open.
const mobileNav = document.querySelector<HTMLDetailsElement>('.nav-mobile');
mobileNav?.addEventListener('toggle', () => {
  document.documentElement.style.overflow = mobileNav.open ? 'hidden' : '';
  const summary = mobileNav.querySelector('summary');
  summary?.setAttribute('aria-label', mobileNav.open ? 'Close menu' : 'Open menu');
});

/* ------------------------- Reading progress bar ------------------------- */

const progress = document.querySelector<HTMLElement>('.read-progress span');
const articleBody = document.querySelector<HTMLElement>('.post-main');
if (progress && articleBody) {
  let queued = false;
  const update = () => {
    queued = false;
    const rect = articleBody.getBoundingClientRect();
    const total = rect.height - window.innerHeight;
    const ratio = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 1;
    progress.style.transform = `scaleX(${ratio})`;
  };
  const onScroll = () => {
    if (!queued) {
      queued = true;
      requestAnimationFrame(update);
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  update();
}

/* ------------------------------ Code copy ------------------------------ */

if (navigator.clipboard) {
  $$<HTMLElement>('.prose .code-block').forEach((block) => {
    const bar = block.querySelector('.code-bar');
    const code = block.querySelector('pre code');
    if (!bar || !code) return;
    const language = bar.textContent?.trim() || 'code';
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'code-copy';
    button.textContent = 'Copy';
    button.setAttribute('aria-label', `Copy ${language} code`);
    button.addEventListener('click', () => {
      void navigator.clipboard.writeText(code.textContent ?? '').then(
        () => {
          button.textContent = 'Copied';
          window.setTimeout(() => (button.textContent = 'Copy'), 1600);
        },
        () => (button.textContent = 'Copy failed'),
      );
    });
    bar.append(button);
  });
}

/* ------------------------- Table of contents ------------------------- */

const tocLinks = new Map<string, HTMLAnchorElement>();
$$<HTMLAnchorElement>('.toc a[href^="#"]').forEach((a) => tocLinks.set(decodeURIComponent(a.hash.slice(1)), a));
if (tocLinks.size && 'IntersectionObserver' in window) {
  let current: HTMLAnchorElement | undefined;
  const observer = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      const link = visible && tocLinks.get(visible.target.id);
      if (!link || link === current) return;
      current?.removeAttribute('aria-current');
      link.setAttribute('aria-current', 'true');
      current = link;
    },
    { rootMargin: '0px 0px -70% 0px' },
  );
  [...tocLinks.keys()].forEach((id) => {
    const el = document.getElementById(id);
    if (el) observer.observe(el);
  });
}

/* --------------------------------- API --------------------------------- */

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await response.json().catch(() => ({}))) as T & { ok?: boolean; error?: string };
  if (!response.ok || data.ok === false) throw new Error(data.error ?? 'Request failed.');
  return data;
}

/* -------------------------------- Likes -------------------------------- */

const LIKES_KEY = 'gs:likes';
const likedSlugs = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(LIKES_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
};
const saveLiked = (set: Set<string>) => {
  try {
    localStorage.setItem(LIKES_KEY, JSON.stringify([...set].slice(-500)));
  } catch {
    /* storage unavailable: likes still count, the state just is not remembered */
  }
};

const likeButtons = $$<HTMLButtonElement>('[data-like]');
if (likeButtons.length) {
  const slug = likeButtons[0]!.dataset.like!;
  const render = (liked: boolean, count?: number) =>
    likeButtons.forEach((b) => {
      b.setAttribute('aria-pressed', String(liked));
      const out = b.querySelector('[data-like-count]');
      if (out && count !== undefined) out.textContent = String(count);
    });
  render(likedSlugs().has(slug));
  likeButtons.forEach((b) => {
    b.hidden = false;
    b.addEventListener('click', async () => {
      const set = likedSlugs();
      const like = !set.has(slug);
      const current = Number(b.querySelector('[data-like-count]')?.textContent ?? '0');
      render(like, Math.max(0, current + (like ? 1 : -1))); // optimistic
      try {
        const { likes } = await postJson<{ likes: number }>('/blog/api/like', { slug, like });
        like ? set.add(slug) : set.delete(slug);
        saveLiked(set);
        render(like, likes);
      } catch {
        render(!like, current);
      }
    });
  });
}

/* -------------------------------- Share -------------------------------- */

// Native share sheet (mobile): title, summary and link, so apps that accept text get all three.
$$<HTMLButtonElement>('[data-native-share]').forEach((button) => {
  if (!('share' in navigator)) return;
  button.hidden = false;
  button.addEventListener('click', () => {
    const data: ShareData = {
      title: button.dataset.shareTitle ?? document.title,
      text: button.dataset.shareText ?? '',
      url: button.dataset.shareUrl ?? (document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null)?.href ?? location.href,
    };
    void navigator.share(data).catch(() => undefined);
  });
});

const flash = (button: HTMLButtonElement, label: string) => {
  const original = button.textContent;
  button.textContent = label;
  window.setTimeout(() => (button.textContent = original), 1600);
};

$$<HTMLButtonElement>('[data-copy-link]').forEach((button) => {
  button.addEventListener('click', () => {
    void navigator.clipboard?.writeText(button.dataset.copyLink ?? location.href).then(() => flash(button, 'Link copied'));
  });
});

// Ready-to-paste post text (title, summary, link, hashtags), e.g. for the LinkedIn app.
$$<HTMLButtonElement>('[data-copy-text]').forEach((button) => {
  button.addEventListener('click', () => {
    void navigator.clipboard?.writeText(button.dataset.copyText ?? '').then(() => flash(button, button.dataset.copiedLabel ?? 'Copied'));
  });
});

/* ------------------------------- Contact ------------------------------- */

// The address is assembled only on click, so it never appears as plain text for harvesters.
$$<HTMLButtonElement>('[data-contact-user]').forEach((button) => {
  button.hidden = false;
  button.addEventListener('click', () => {
    const { contactUser, contactDomain, contactTitle, contactUrl } = button.dataset;
    const subject = encodeURIComponent(`About: ${contactTitle ?? document.title}`);
    const body = encodeURIComponent(`\n\n${contactUrl ?? location.href}`);
    window.location.href = `mailto:${contactUser}@${contactDomain}?subject=${subject}&body=${body}`;
  });
});

/* ---------------------------- Report an issue ---------------------------- */

const dialog = document.querySelector<HTMLDialogElement>('#report-dialog');
const reportForm = dialog?.querySelector<HTMLFormElement>('[data-report-form]');
const reportStatus = dialog?.querySelector<HTMLElement>('[data-report-status]');
let openedAt = 0;

if (dialog && reportForm && typeof dialog.showModal === 'function') {
  $$<HTMLButtonElement>('[data-report-open]').forEach((button) => {
    button.hidden = false;
    button.addEventListener('click', () => {
      openedAt = Date.now();
      if (reportStatus) {
        reportStatus.textContent = '';
        delete reportStatus.dataset.state;
      }
      dialog.showModal();
    });
  });
  dialog.querySelector('[data-report-cancel]')?.addEventListener('click', () => dialog.close());

  reportForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(reportForm);
    const submit = reportForm.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.disabled = true;
    try {
      await postJson('/blog/api/report', {
        slug: reportForm.dataset.slug,
        reason: data.get('reason'),
        message: data.get('message'),
        email: data.get('email') ?? '',
        website: data.get('website') ?? '',
        elapsed: Date.now() - openedAt,
      });
      reportForm.reset();
      if (reportStatus) reportStatus.textContent = 'Thank you. Your report was sent and I will review it.';
      window.setTimeout(() => dialog.close(), 2200);
    } catch (err) {
      if (reportStatus) {
        reportStatus.dataset.state = 'error';
        reportStatus.textContent = err instanceof Error ? err.message : 'The report could not be sent.';
      }
    } finally {
      if (submit) submit.disabled = false;
    }
  });
}

/* -------------------------------- Comments -------------------------------- */

const commentForm = document.querySelector<HTMLFormElement>('[data-comment-form]');
if (commentForm) {
  const shownAt = Date.now();
  const status = commentForm.querySelector<HTMLElement>('[data-comment-status]');
  const parentInput = commentForm.querySelector<HTMLInputElement>('input[name="parentId"]')!;
  const replying = commentForm.querySelector<HTMLElement>('[data-replying]');
  const replyingName = commentForm.querySelector<HTMLElement>('[data-replying-name]');
  const nameInput = commentForm.querySelector<HTMLInputElement>('input[name="name"]');
  const NAME_KEY = 'gs:comment-name';
  const setStatus = (text: string, error = false) => {
    if (!status) return;
    status.textContent = text;
    if (error) status.dataset.state = 'error';
    else delete status.dataset.state;
  };

  commentForm.hidden = false;

  // Links to #comment-form (e.g. "Ask a follow-up question") scroll to the form and put the cursor in it.
  const openForm = () => {
    commentForm.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    commentForm.querySelector<HTMLTextAreaElement>('textarea')?.focus({ preventScroll: true });
  };
  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href="#comment-form"]');
    if (!link) return;
    e.preventDefault();
    history.replaceState(null, '', '#comment-form');
    openForm();
  });
  // Arriving with #comment-form: the form was still hidden when the browser tried to scroll.
  if (location.hash === '#comment-form') requestAnimationFrame(openForm);
  try {
    if (nameInput && !nameInput.value) nameInput.value = localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    /* storage unavailable */
  }

  // Reply: reuse the single form, remembering which comment it answers.
  $$<HTMLButtonElement>('[data-reply-to]').forEach((button) => {
    button.hidden = false;
    button.addEventListener('click', () => {
      parentInput.value = button.dataset.replyTo ?? '';
      if (replyingName) replyingName.textContent = button.dataset.replyName ?? '';
      if (replying) replying.hidden = false;
      commentForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
      commentForm.querySelector<HTMLTextAreaElement>('textarea')?.focus({ preventScroll: true });
    });
  });
  commentForm.querySelector('[data-reply-cancel]')?.addEventListener('click', () => {
    parentInput.value = '';
    if (replying) replying.hidden = true;
  });

  commentForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const data = new FormData(commentForm);
    const submit = commentForm.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submit) submit.disabled = true;
    setStatus('Sending…');
    try {
      await postJson('/blog/api/comment', {
        slug: commentForm.dataset.slug,
        parentId: data.get('parentId') ?? '',
        name: data.get('name'),
        email: data.get('email') ?? '',
        body: data.get('body'),
        website: data.get('website') ?? '',
        elapsed: Date.now() - shownAt,
      });
      try {
        localStorage.setItem(NAME_KEY, String(data.get('name') ?? ''));
      } catch {
        /* ignore */
      }
      commentForm.querySelector<HTMLTextAreaElement>('textarea')!.value = '';
      parentInput.value = '';
      if (replying) replying.hidden = true;
      setStatus('Thank you. Your comment was received and will appear once it has been reviewed.');
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Your comment could not be sent.', true);
    } finally {
      if (submit) submit.disabled = false;
    }
  });
}

// "Report" on a comment opens the issue dialog, pre-filled to identify the comment.
$$<HTMLButtonElement>('[data-report-comment]').forEach((button) => {
  if (!dialog || !reportForm) return;
  button.hidden = false;
  button.addEventListener('click', () => {
    const reason = reportForm.querySelector<HTMLSelectElement>('select[name="reason"]');
    const message = reportForm.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
    if (reason) reason.value = 'comment';
    if (message) message.value = `Comment by ${button.dataset.reportName ?? 'a reader'} (#comment-${button.dataset.reportComment}): `;
    openedAt = Date.now();
    dialog.showModal();
    message?.focus();
  });
});

/* ------------------------------ Glossary filter ------------------------------ */

const glossaryFilter = document.querySelector<HTMLInputElement>('[data-glossary-filter]');
if (glossaryFilter) {
  glossaryFilter.hidden = false;
  const entries = $$<HTMLElement>('[data-glossary-entry]');
  const groups = $$<HTMLElement>('[data-glossary-group]');
  const empty = document.querySelector<HTMLElement>('[data-glossary-empty]');
  glossaryFilter.addEventListener('input', () => {
    const q = glossaryFilter.value.trim().toLowerCase();
    let shown = 0;
    entries.forEach((e) => {
      const match = !q || (e.dataset.search ?? '').includes(q);
      e.hidden = !match;
      if (match) shown++;
    });
    groups.forEach((g) => (g.hidden = !g.querySelector('[data-glossary-entry]:not([hidden])')));
    if (empty) empty.hidden = shown > 0;
  });
}

/* ------------------------------- Back to top ------------------------------- */

const toTop = document.querySelector<HTMLAnchorElement>('[data-to-top]');
if (toTop) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let shown = false;
  const update = () => {
    const show = window.scrollY > 700;
    if (show !== shown) {
      shown = show;
      toTop.classList.toggle('is-visible', show);
      toTop.tabIndex = show ? 0 : -1;
    }
  };
  window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
  toTop.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    // Move focus to the top for keyboard and screen-reader users.
    document.querySelector<HTMLElement>('#main, main')?.focus({ preventScroll: true });
  });
  update();
}

/* ------------------------- "Did this answer your question?" ------------------------- */

document.querySelectorAll<HTMLElement>('[data-helpful]').forEach((box) => {
  const slug = box.dataset.helpful ?? '';
  const key = `gs_helpful_${slug}`;
  const thanks = box.querySelector<HTMLElement>('[data-helpful-thanks]');
  const done = () => {
    box.querySelector('.helpful-actions')?.setAttribute('hidden', '');
    if (thanks) thanks.hidden = false;
  };
  if (sessionStorage.getItem(key)) done();
  box.querySelectorAll<HTMLButtonElement>('[data-helpful-vote]').forEach((b) =>
    b.addEventListener('click', () => {
      void fetch('/blog/api/helpful', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ slug, v: b.dataset.helpfulVote }),
      }).catch(() => undefined);
      try {
        sessionStorage.setItem(key, '1');
      } catch {
        /* ignore */
      }
      done();
    }),
  );
});

/* ------------------------------- Newsletter ------------------------------- */

document.querySelectorAll<HTMLFormElement>('[data-subscribe-form]').forEach((form) => {
  const started = form.querySelector<HTMLInputElement>('[data-started]');
  if (started) started.value = String(Date.now());
  const status = form.querySelector<HTMLElement>('[data-subscribe-status]');
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = form.querySelector<HTMLInputElement>('input[name="email"]');
    if (!email?.value || !email.checkValidity()) {
      if (status) {
        status.textContent = 'Please enter a valid email address.';
        status.dataset.state = 'error';
      }
      email?.focus();
      return;
    }
    const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (button) button.disabled = true;
    try {
      const res = await fetch('/blog/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          email: email.value,
          website: form.querySelector<HTMLInputElement>('input[name="website"]')?.value ?? '',
          t: Number(started?.value ?? 0),
          source: location.pathname,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; message?: string };
      if (status) {
        status.textContent = data.message ?? (res.ok ? 'Check your inbox to confirm.' : 'Something went wrong. Please try again.');
        status.dataset.state = res.ok ? 'ok' : 'error';
      }
      if (res.ok) email.value = '';
    } catch {
      if (status) {
        status.textContent = 'Network error. Please try again.';
        status.dataset.state = 'error';
      }
    } finally {
      if (button) button.disabled = false;
    }
  });
});

/* --------------------------- Push notifications --------------------------- */
// Nothing is requested from the browser until the reader taps the button. The permission
// request must happen immediately on that tap: mobile browsers (Safari especially) ignore
// it once the tap is "used up" by earlier asynchronous work.

const pushRow = document.querySelector<HTMLElement>('[data-push]');
if (pushRow) {
  const button = pushRow.querySelector<HTMLButtonElement>('[data-push-toggle]')!;
  const label = pushRow.querySelector<HTMLElement>('[data-push-label]')!;
  const status = pushRow.querySelector<HTMLElement>('[data-push-status]')!;
  const key = pushRow.dataset.key ?? '';
  const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  if (!supported) {
    // iPhone and iPad support web push only for sites added to the home screen.
    if (isIos && !standalone) {
      pushRow.hidden = false;
      button.disabled = true;
      status.textContent = 'On iPhone and iPad: tap Share → Add to Home Screen, open the site from the home screen, then turn on notifications there.';
    }
  } else {
    pushRow.hidden = false;
    let subscribed = false;

    const toKey = (b64: string) => {
      const raw = atob((b64 + '='.repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/'));
      return Uint8Array.from(raw, (c) => c.charCodeAt(0));
    };
    const post = (url: string, body: unknown) =>
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify(body) });
    const withTimeout = <T,>(p: Promise<T>, ms: number, what: string) =>
      Promise.race([p, new Promise<never>((_, reject) => window.setTimeout(() => reject(Object.assign(new Error(`${what} timed out`), { name: 'TimeoutError' })), ms))]);

    /** Registers the service worker and waits until it is active (with a timeout). */
    const activeRegistration = async (): Promise<ServiceWorkerRegistration> => {
      const reg = await withTimeout(navigator.serviceWorker.register('/sw.js', { scope: '/' }), 15_000, 'Service worker registration');
      if (reg.active) return reg;
      const worker = reg.installing ?? reg.waiting;
      if (!worker) return withTimeout(navigator.serviceWorker.ready, 15_000, 'Service worker activation');
      await withTimeout(
        new Promise<void>((resolve, reject) => {
          worker.addEventListener('statechange', () => {
            if (worker.state === 'activated') resolve();
            if (worker.state === 'redundant') reject(new Error('Service worker failed to install'));
          });
        }),
        15_000,
        'Service worker activation',
      );
      return reg;
    };

    const explain = (err: unknown): string => {
      console.error('Push notifications:', err);
      const name = (err as { name?: string })?.name ?? '';
      const message = String((err as { message?: string })?.message ?? '');
      if (name === 'TimeoutError') return 'Setting up notifications took too long. Check your connection and try again.';
      if (name === 'NotAllowedError') return 'Notifications are blocked for this site in your browser settings.';
      if ((navigator as unknown as { brave?: unknown }).brave) return 'Brave turns off push messaging by default. Enable "Use Google services for push messaging" under brave://settings/privacy, then try again.';
      if (name === 'AbortError' || /push service/i.test(message)) return 'Your browser\'s push service is unavailable (often switched off in private windows or by privacy settings). Try a normal window.';
      if (/applicationServerKey|InvalidAccess|InvalidCharacter/i.test(`${name} ${message}`)) return 'Notifications are not set up correctly on this site yet. Please try again later.';
      return 'Notifications could not be turned on in this browser.';
    };

    const refresh = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration('/');
        subscribed = Boolean(await reg?.pushManager.getSubscription());
      } catch {
        subscribed = false;
      }
      label.textContent = subscribed ? 'Turn off notifications' : 'Get browser notifications';
      button.setAttribute('aria-pressed', subscribed ? 'true' : 'false');
      if (!subscribed && Notification.permission === 'denied') status.textContent = 'Notifications are blocked for this site in your browser settings.';
    };

    button.addEventListener('click', () => {
      if (button.disabled) return;
      // Ask first, synchronously inside the tap; everything else happens afterwards.
      const permission: Promise<NotificationPermission> = subscribed
        ? Promise.resolve(Notification.permission)
        : Notification.permission === 'granted'
          ? Promise.resolve('granted')
          : Notification.requestPermission();
      button.disabled = true;
      status.textContent = subscribed ? 'Turning notifications off…' : 'Waiting for your permission…';

      void (async () => {
        try {
          if (subscribed) {
            const reg = await navigator.serviceWorker.getRegistration('/');
            const existing = await reg?.pushManager.getSubscription();
            if (existing) {
              await post('/blog/api/push/unsubscribe', { endpoint: existing.endpoint });
              await existing.unsubscribe();
            }
            status.textContent = 'Notifications are off.';
            return;
          }
          const result = await permission;
          if (result !== 'granted') {
            status.textContent = result === 'denied' ? 'Notifications are blocked for this site in your browser settings.' : 'Notifications were not allowed.';
            return;
          }
          status.textContent = 'Setting up notifications…';
          const reg = await activeRegistration();
          const sub = (await reg.pushManager.getSubscription()) ?? (await withTimeout(reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(key) }), 20_000, 'Push subscription'));
          const res = await post('/blog/api/push/subscribe', sub.toJSON());
          if (res.ok) status.textContent = 'Done. You will be notified about new articles.';
          else {
            console.error('Push notifications: the server rejected the subscription', res.status);
            await sub.unsubscribe().catch(() => undefined);
            status.textContent = 'Notifications could not be saved. Please try again later.';
          }
        } catch (err) {
          status.textContent = explain(err);
        } finally {
          button.disabled = false;
          void refresh();
        }
      })();
    });
    void refresh();
  }
}

/* --------------------- Clean-up of former Google Analytics cookies --------------------- */
// Google Analytics was removed; delete any _ga/_gid cookies it set earlier.
{
  const host = location.hostname;
  for (const pair of document.cookie.split(';')) {
    const name = pair.split('=')[0]?.trim() ?? '';
    if (!/^(_ga|_gid|_gat)/.test(name)) continue;
    for (const d of ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`]) document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ''}`;
  }
}

/* ------------------------- Consent and third-party tracking ------------------------- */
// Trackers (custom snippets) run only after the visitor accepts. The choice is
// stored locally (strictly necessary to remember it) together with a version: when the
// configured trackers change, the visitor is asked again. Rejecting is as easy as accepting.

const trackingConfigEl = document.getElementById('gs-tracking');
if (trackingConfigEl) {
  const cfg = JSON.parse(trackingConfigEl.textContent || '{}') as { version: string };
  const KEY = 'gs_consent';
  const banner = document.getElementById('gs-consent');
  let loaded = false;

  const readChoice = (): 'granted' | 'denied' | null => {
    try {
      const v = JSON.parse(localStorage.getItem(KEY) ?? 'null') as { value?: string; version?: string } | null;
      return v?.version === cfg.version && (v.value === 'granted' || v.value === 'denied') ? v.value : null;
    } catch {
      return null;
    }
  };

  const loadTrackers = () => {
    if (loaded) return;
    loaded = true;
    const nonce = document.querySelector<HTMLMetaElement>('meta[name="gs-csp-nonce"]')?.content ?? '';
    // Custom snippets: copied out of the inert <template>; scripts are recreated so they execute.
    const template = document.getElementById('gs-consent-scripts') as HTMLTemplateElement | null;
    template?.content.childNodes.forEach((node) => {
      if (node instanceof HTMLScriptElement) {
        const script = document.createElement('script');
        for (const attr of Array.from(node.attributes)) if (attr.name !== 'nonce') script.setAttribute(attr.name, attr.value);
        script.textContent = node.textContent;
        script.nonce = nonce;
        document.head.append(script);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        document.head.append(node.cloneNode(true));
      }
    });
  };

  /** Removes analytics cookies (e.g. _ga, _ga_XXXX, _gid) from this host and its parent domain. */
  const clearTrackingCookies = () => {
    const host = location.hostname;
    const domains = ['', host, `.${host}`, `.${host.replace(/^www\./, '')}`];
    for (const pair of document.cookie.split(';')) {
      const name = pair.split('=')[0]?.trim() ?? '';
      if (!/^(_ga|_gid|_gat|_gcl)/.test(name)) continue;
      for (const d of domains) document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ''}`;
    }
  };

  const choose = (value: 'granted' | 'denied') => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ value, version: cfg.version, at: new Date().toISOString() }));
    } catch {
      /* storage unavailable: the banner simply shows again next time */
    }
    if (banner) banner.hidden = true;
    if (value === 'granted') loadTrackers();
    else {
      clearTrackingCookies();
      if (loaded) location.reload(); // stop trackers that are already running
    }
  };

  document.querySelectorAll<HTMLButtonElement>('[data-consent]').forEach((b) =>
    b.addEventListener('click', () => choose(b.dataset.consent === 'granted' ? 'granted' : 'denied')),
  );
  document.querySelectorAll<HTMLButtonElement>('[data-consent-open]').forEach((b) =>
    b.addEventListener('click', () => {
      if (banner) {
        banner.hidden = false;
        banner.querySelector<HTMLButtonElement>('[data-consent]')?.focus();
      }
    }),
  );

  const choice = readChoice();
  const gpc = (navigator as unknown as { globalPrivacyControl?: boolean }).globalPrivacyControl === true;
  if (choice === 'granted') loadTrackers();
  else if (choice === null) {
    if (gpc) choose('denied'); // the browser signals an opt-out: respect it without asking
    else if (banner) banner.hidden = false;
  }
}

/* ------------------------------- Analytics ------------------------------- */

// Cookieless page-view beacon: path, referrer and campaign source only.
if (!document.body.hasAttribute('data-no-track')) {
  const params = new URLSearchParams(location.search);
  const payload = JSON.stringify({ p: location.pathname, r: document.referrer, s: params.get('utm_source') ?? '' });
  const blob = new Blob([payload], { type: 'application/json' });
  if (!(navigator.sendBeacon && navigator.sendBeacon('/blog/api/hit', blob))) {
    void fetch('/blog/api/hit', { method: 'POST', body: payload, headers: { 'Content-Type': 'application/json' }, keepalive: true, credentials: 'same-origin' }).catch(() => undefined);
  }

  // Read completion: 75% of the text scrolled past and at least 20 seconds on the page, sent once.
  const prose = document.querySelector<HTMLElement>('.post-main .prose, .glossary-term .prose');
  if (prose) {
    const started = Date.now();
    let sent = false;
    const check = () => {
      if (sent || document.visibilityState !== 'visible') return;
      const rect = prose.getBoundingClientRect();
      const progressed = (window.innerHeight - rect.top) / Math.max(rect.height, 1);
      if (progressed >= 0.75 && Date.now() - started >= 20_000) {
        sent = true;
        const body = JSON.stringify({ p: location.pathname });
        if (!(navigator.sendBeacon && navigator.sendBeacon('/blog/api/read', new Blob([body], { type: 'application/json' })))) {
          void fetch('/blog/api/read', { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true, credentials: 'same-origin' }).catch(() => undefined);
        }
        window.removeEventListener('scroll', onScroll);
        clearInterval(timer);
      }
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        check();
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    const timer = window.setInterval(check, 5_000); // short articles may be fully visible without scrolling
  }
}
